import React, { useEffect, useRef } from 'react';
import anime from 'animejs';
import { SpeedUnit } from '../../types';

interface SpeedometerProps {
  speed: number;
  speedUnit: SpeedUnit;
  acceleration: number;
  maxSpeed?: number;
}

export const IceEcoSpeedometer: React.FC<SpeedometerProps> = ({
  speed,
  speedUnit,
  acceleration,
  maxSpeed = 220,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const ringRef = useRef<SVGCircleElement | null>(null);
  const speedTextRef = useRef<HTMLSpanElement | null>(null);
  const prevSpeedRef = useRef<number>(0);

  const radius = 120;
  const circumference = 2 * Math.PI * radius;
  const clampedSpeed = Math.max(0, Math.min(maxSpeed, speed));
  const progressRatio = clampedSpeed / maxSpeed;
  const strokeDashoffset = circumference * (1 - progressRatio * 0.75); // 270 degree arc

  useEffect(() => {
    if (!ringRef.current) return;
    anime({
      targets: ringRef.current,
      strokeDashoffset,
      duration: 700,
      easing: 'easeOutQuad',
    });
  }, [strokeDashoffset]);

  useEffect(() => {
    if (!speedTextRef.current) return;
    const obj = { val: prevSpeedRef.current };
    anime({
      targets: obj,
      val: speed,
      round: 1,
      duration: 600,
      easing: 'easeOutQuad',
      update: () => {
        if (speedTextRef.current) {
          speedTextRef.current.textContent = String(Math.round(obj.val));
        }
      },
    });
    prevSpeedRef.current = speed;
  }, [speed]);

  const accelBarWidth = Math.max(0, Math.min(100, (acceleration + 0.5) * 100));

  return (
    <div
      ref={containerRef}
      className="relative w-72 h-72 md:w-84 md:h-84 flex flex-col items-center justify-center select-none"
    >
      {/* Subtle Eco Energy Backdrop */}
      <div className="absolute inset-4 rounded-full bg-emerald-950/20 blur-2xl pointer-events-none" />

      {/* SVG Thin Circular Arc */}
      <svg className="w-full h-full transform -rotate-135" viewBox="0 0 300 300">
        {/* Background Track */}
        <circle
          cx="150"
          cy="150"
          r={radius}
          fill="none"
          stroke="rgba(16, 185, 129, 0.12)"
          strokeWidth="4"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * 0.25}
          strokeLinecap="round"
        />
        {/* Dynamic Progress Ring */}
        <circle
          ref={ringRef}
          cx="150"
          cy="150"
          r={radius}
          fill="none"
          stroke="#10b981"
          strokeWidth="5"
          strokeDasharray={circumference}
          strokeDashoffset={circumference}
          strokeLinecap="round"
          className="filter drop-shadow-[0_0_8px_rgba(16,185,129,0.5)]"
        />
      </svg>

      {/* Central Content */}
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
        <span className="text-[11px] font-mono tracking-[0.25em] text-emerald-500/80 uppercase font-semibold">
          ECO EFFICIENCY
        </span>
        <span
          ref={speedTextRef}
          className="text-7xl md:text-8xl font-black font-display text-white tracking-tighter drop-shadow-[0_2px_12px_rgba(16,185,129,0.3)] my-1"
        >
          {Math.round(speed)}
        </span>
        <span className="text-xs font-mono tracking-widest text-emerald-400 font-bold uppercase">
          {speedUnit.toUpperCase()}
        </span>

        {/* Subtle Horizontal Acceleration Indicator */}
        <div className="w-32 mt-4 flex flex-col items-center gap-1">
          <div className="w-full h-1 bg-slate-900 rounded-full overflow-hidden border border-emerald-900/40">
            <div
              className="h-full bg-gradient-to-r from-emerald-600 to-teal-400 transition-all duration-300 rounded-full"
              style={{ width: `${accelBarWidth}%` }}
            />
          </div>
          <span className="text-[9px] font-mono text-emerald-500/60 uppercase tracking-widest">
            ACCEL LOAD
          </span>
        </div>
      </div>
    </div>
  );
};
